import * as Sentry from '@sentry/react';
import type { ErrorEvent, Event } from '@sentry/react';
import { makeBrowserOfflineTransport, makeFetchTransport } from '@sentry/react';
import type { TokenDiagnosticOutcome } from '@/types/errorReporter';
import type { TokenRequestDiagnostics } from '@/utils/helpers/tokenResponseDiagnostics';
import {
  sanitizeTokenRecoveryDiagnostic,
  type TokenRecoveryReason
} from '@/utils/helpers/tokenRecoveryDiagnostics';

const CHAT_DOMAIN_TAG = 'chat';
const TOKEN_DIAGNOSTIC_TAG = 'digitChatTokenDiagnostic';
const TOKEN_DIAGNOSTIC_ERROR_NAME = 'DigitChatTokenRequestError';
const TOKEN_DIAGNOSTIC_ERROR_MESSAGE = 'Digit Chat token request failed';

const TOKEN_REQUEST_OPERATIONS = new Set(['bootstrap', 'refresh']);
const TOKEN_REQUEST_FAILURE_STAGES = new Set([
  'transport',
  'http',
  'response',
  'access_token',
  'jwt',
  'state',
  'coordination'
]);
const TOKEN_REQUEST_TRANSPORT_FAILURES = new Set(['watchdog_timeout', 'cancelled', 'no_response']);
const TOKEN_REQUEST_JWT_FAILURES = new Set([
  'expired_or_insufficient_lifetime',
  'invalid_claims',
  'stale_in_transit',
  'user_mismatch'
]);
const TOKEN_DIAGNOSTIC_OUTCOMES = new Set<TokenDiagnosticOutcome>([
  'non_retryable_failure',
  'recovery_exhausted',
  'terminal_failure'
]);

const sanitizeTokenRequestContext = (context: unknown): Record<string, unknown> | null => {
  if (!context || typeof context !== 'object' || Array.isArray(context)) return null;

  const tokenRequest = context as Record<string, unknown>;
  const operation = TOKEN_REQUEST_OPERATIONS.has(String(tokenRequest.operation))
    ? String(tokenRequest.operation)
    : undefined;
  const responseReceived =
    typeof tokenRequest.responseReceived === 'boolean' ? tokenRequest.responseReceived : undefined;
  const status =
    typeof tokenRequest.status === 'number' &&
    Number.isInteger(tokenRequest.status) &&
    tokenRequest.status >= 100 &&
    tokenRequest.status <= 599
      ? tokenRequest.status
      : undefined;
  const transport = TOKEN_REQUEST_TRANSPORT_FAILURES.has(String(tokenRequest.transport))
    ? String(tokenRequest.transport)
    : undefined;
  const failureAt = TOKEN_REQUEST_FAILURE_STAGES.has(String(tokenRequest.failureAt))
    ? String(tokenRequest.failureAt)
    : undefined;
  const jwtFailure = TOKEN_REQUEST_JWT_FAILURES.has(String(tokenRequest.jwtFailure))
    ? String(tokenRequest.jwtFailure)
    : undefined;

  if (!operation || responseReceived === undefined || !failureAt) return null;

  return {
    operation,
    responseReceived,
    ...(status !== undefined && { status }),
    ...(transport && { transport }),
    ...(jwtFailure && { jwtFailure }),
    failureAt
  };
};

const getTokenDiagnosticOutcome = (event: Event): TokenDiagnosticOutcome | null => {
  const outcome = event.tags?.tokenOutcome;

  return TOKEN_DIAGNOSTIC_OUTCOMES.has(outcome as TokenDiagnosticOutcome)
    ? (outcome as TokenDiagnosticOutcome)
    : null;
};

const getTokenDiagnosticFingerprint = (
  tokenRequest: Record<string, unknown>,
  outcome: TokenDiagnosticOutcome,
  coordinationFailure?: TokenRecoveryReason
): string[] => {
  let failureClass = String(
    coordinationFailure ||
      tokenRequest.transport ||
      tokenRequest.jwtFailure ||
      tokenRequest.failureAt
  );

  if (tokenRequest.failureAt === 'http' && tokenRequest.status !== undefined) {
    failureClass = `http_${tokenRequest.status}`;
  }

  return ['digit-chat-token', String(tokenRequest.operation), outcome, failureClass];
};

export const isTokenDiagnosticEvent = (event: Event): boolean =>
  event.tags?.[TOKEN_DIAGNOSTIC_TAG] === 'true';

const sanitizeRuntime = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }

  const runtime = value as Record<string, unknown>;
  const result: Record<string, string> = {};

  if (
    typeof runtime.hostname === 'string' &&
    runtime.hostname.length <= 253 &&
    /^(localhost|(?:[a-z0-9-]+\.)*(?:turbo\.az|azstaging\.net|azstage\.in|localhost))$/.test(
      runtime.hostname
    )
  ) {
    result.hostname = runtime.hostname;
  }

  if (['Edge', 'Chrome', 'Firefox', 'Safari'].includes(String(runtime.browser))) {
    result.browser = String(runtime.browser);

    if (
      typeof runtime.browserVersion === 'string' &&
      /^\d{1,4}(?:\.\d{1,6}){0,3}$/.test(runtime.browserVersion)
    ) {
      result.browserVersion = runtime.browserVersion;
    }
  }

  if (['Windows', 'macOS', 'Linux', 'Android', 'iOS'].includes(String(runtime.os))) {
    result.os = String(runtime.os);
  }

  return result;
};

const getRuntime = (): Record<string, string> => {
  const agent = navigator.userAgent;
  const browsers = [
    ['Edge', /Edg\/([\d.]+)/],
    ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
    ['Safari', /Version\/([\d.]+)/]
  ] as const;
  const browser = browsers.find(([, pattern]) => pattern.test(agent));
  const platforms = [
    ['iOS', /iPhone|iPad/],
    ['Android', /Android/],
    ['Windows', /Windows/],
    ['macOS', /Macintosh/],
    ['Linux', /Linux/]
  ] as const;
  const os = platforms.find(([, pattern]) => pattern.test(agent));

  return sanitizeRuntime({
    hostname: window.location.hostname,
    ...(browser && { browser: browser[0], browserVersion: agent.match(browser[1])?.[1] }),
    ...(os && { os: os[0] })
  });
};

export function sanitizeTokenDiagnosticEvent(event: ErrorEvent): ErrorEvent;
export function sanitizeTokenDiagnosticEvent(event: Event): Event;
export function sanitizeTokenDiagnosticEvent(event: Event): Event {
  if (!isTokenDiagnosticEvent(event)) {
    return event;
  }

  const tokenRequest = sanitizeTokenRequestContext(event.contexts?.tokenRequest);
  const outcome = getTokenDiagnosticOutcome(event);
  const recovery = sanitizeTokenRecoveryDiagnostic(event.contexts?.tokenRecovery);
  const runtime = recovery ? sanitizeRuntime(event.contexts?.runtime) : {};
  const isStaging = /\.(?:azstaging\.net|azstage\.in)$/.test(runtime.hostname || '');
  const tags: NonNullable<Event['tags']> = {
    domain: CHAT_DOMAIN_TAG,
    [TOKEN_DIAGNOSTIC_TAG]: 'true',
    ...(outcome && { tokenOutcome: outcome }),
    ...(event.tags?.chatRelease && { chatRelease: event.tags.chatRelease })
  };

  if (recovery) {
    tags.chatRecoveryId = recovery.recoveryId;
    tags.refreshTrigger = recovery.trigger;
    tags.refreshRole = recovery.role;

    if (recovery.reason) {
      tags.refreshFailure = recovery.reason;
    }
  }

  if (runtime.hostname) {
    tags.deploymentHost = runtime.hostname;
  }

  const sanitizedEvent: Event = {
    ...(event.event_id && { event_id: event.event_id }),
    ...(event.timestamp !== undefined && { timestamp: event.timestamp }),
    ...(event.level && { level: event.level }),
    ...(event.platform && { platform: event.platform }),
    ...(event.release && { release: event.release }),
    ...(event.environment && { environment: isStaging ? 'staging' : event.environment }),
    exception: {
      values: [
        {
          type: TOKEN_DIAGNOSTIC_ERROR_NAME,
          value: TOKEN_DIAGNOSTIC_ERROR_MESSAGE
        }
      ]
    },
    tags
  };

  if (tokenRequest) {
    sanitizedEvent.contexts = { tokenRequest };

    if (recovery) {
      sanitizedEvent.contexts.tokenRecovery = { ...recovery };
      sanitizedEvent.contexts.runtime = runtime;
    }
  }

  if (tokenRequest && outcome) {
    sanitizedEvent.fingerprint = getTokenDiagnosticFingerprint(
      tokenRequest,
      outcome,
      recovery?.reason
    );
  }

  return sanitizedEvent;
}

let tokenDiagnosticClient: Sentry.BrowserClient | null | undefined;
const createTokenDiagnosticOfflineTransport = makeBrowserOfflineTransport(makeFetchTransport);

const getTokenDiagnosticClient = (): Sentry.BrowserClient | null => {
  if (tokenDiagnosticClient !== undefined) return tokenDiagnosticClient;

  const dsn = process.env.SENTRY_DSN;

  if (!dsn) {
    tokenDiagnosticClient = null;
    return tokenDiagnosticClient;
  }

  tokenDiagnosticClient = new Sentry.BrowserClient({
    dsn,
    environment: process.env.NODE_ENV,
    release: process.env.CHAT_SENTRY_RELEASE,
    integrations: [],
    enableLogs: false,
    enableMetrics: false,
    normalizeDepth: 3,
    sendClientReports: false,
    sendDefaultPii: false,
    stackParser: Sentry.defaultStackParser,
    transport: options =>
      createTokenDiagnosticOfflineTransport({
        ...options,
        dbName: 'digit-chat-token-diagnostics',
        storeName: 'queue'
      }),
    transportOptions: {
      fetchOptions: {
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      }
    }
  });
  tokenDiagnosticClient.init();

  return tokenDiagnosticClient;
};

const createTokenDiagnosticEventId = (): string => {
  const randomBytes = new Uint8Array(16);

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(randomBytes);
    return Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  return Array.from({ length: 4 }, () => Math.random().toString(16).slice(2).padEnd(8, '0'))
    .join('')
    .slice(0, 32);
};

export const reportTokenRequestDiagnostic = (
  tokenRequest: TokenRequestDiagnostics,
  outcome: TokenDiagnosticOutcome
): void => {
  try {
    const client = getTokenDiagnosticClient();

    if (!client) return;

    client.sendEvent(
      sanitizeTokenDiagnosticEvent({
        event_id: createTokenDiagnosticEventId(),
        timestamp: Date.now() / 1000,
        environment: process.env.NODE_ENV,
        release: process.env.CHAT_SENTRY_RELEASE,
        level: 'error',
        exception: {
          values: [
            {
              type: TOKEN_DIAGNOSTIC_ERROR_NAME,
              value: TOKEN_DIAGNOSTIC_ERROR_MESSAGE
            }
          ]
        },
        tags: {
          domain: CHAT_DOMAIN_TAG,
          [TOKEN_DIAGNOSTIC_TAG]: 'true',
          tokenOutcome: outcome,
          ...(process.env.CHAT_SENTRY_RELEASE && {
            chatRelease: process.env.CHAT_SENTRY_RELEASE
          })
        },
        contexts: {
          tokenRequest: { ...tokenRequest },
          ...(tokenRequest.recovery && {
            tokenRecovery: { ...tokenRequest.recovery },
            runtime: getRuntime()
          })
        }
      })
    );
  } catch (_error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to report Digit Chat token request diagnostic');
    }
  }
};
